/**
 * 动画事件总线 —— 把 WS 的 `event` 帧分发给订阅者 (AnimationLayer / Seat 等)。
 *
 * 设计要点:
 * - 同步 emit, 不引入异步 / RxJS;
 * - 订阅者按 kind 注册回调, 返回 unsubscribe 函数 (Vue 风格);
 * - 单例 (module-scope), 因为同时只有一个 RoomPage / AnimationLayer 存活。
 *
 * 与 RoomEventFrame 的关系: bus 直接接收 frame.data + frame.kind, 不做加工。
 */

import type { RoomEventKind } from '@/ws/frames'

export type AnimEventHandler = (data: Record<string, unknown>) => void

const handlers = new Map<RoomEventKind | '*', Set<AnimEventHandler>>()

function emit(kind: RoomEventKind, data: Record<string, unknown>): void {
  const set = handlers.get(kind)
  if (set) {
    for (const h of set) {
      try { h(data) } catch (e) { console.error('[anim-bus] handler error', e) }
    }
  }
  const all = handlers.get('*')
  if (all) {
    for (const h of all) {
      try { h({ ...data, _kind: kind }) } catch (e) { console.error('[anim-bus] handler error', e) }
    }
  }
}

function on(kind: RoomEventKind | '*', cb: AnimEventHandler): () => void {
  let set = handlers.get(kind)
  if (!set) {
    set = new Set()
    handlers.set(kind, set)
  }
  set.add(cb)
  return () => {
    set!.delete(cb)
    if (set!.size === 0) handlers.delete(kind)
  }
}

function clear(): void {
  handlers.clear()
}

export function useAnimationBus() {
  return { emit, on, clear }
}
